import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFilePdf, 
    faFileWord, 
    faFileExcel, 
    faFileImage, 
    faFile,
    faFileCode,
    faFileArchive,
    faTimes,
    faChevronLeft
} from '@fortawesome/free-solid-svg-icons';
import DocumentNameSuggestionForm from './DocumentNaming/DocumentNameSuggestionForm';
import FileDropZone from './shared/FileDropZone';
import { smartCaseNode } from '../utils/smartCase';
import './DeliveryDocumentUploadModal.css';

/* Single source of truth for what this window will take. The file picker's
   `accept` list and the drag-and-drop check are both built from it, so the two
   doors into this form cannot disagree about which files are allowed — the
   drop door previously had no opinion at all because it had no code. */
const ACCEPTED_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'pptx', 'md', 'html', 'csv',
    'png', 'jpg', 'jpeg', 'tiff', 'bmp', 'webp', 'txt', 'eml', 'msg', 'adoc',
];
const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',');
const MAX_FILE_BYTES = 30 * 1024 * 1024;

/** Returns a refusal message, or null when the file is fine. */
const rejectionReasonFor = (file) => {
    if (!file) return 'No file was detected.';
    const extension = (file.name || '').split('.').pop().toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
        return `"${file.name}" is not a file type this window accepts. Supported: ${ACCEPTED_EXTENSIONS.join(', ').toUpperCase()}.`;
    }
    if (file.size > MAX_FILE_BYTES) {
        return `"${file.name}" is larger than the 30MB limit.`;
    }
    return null;
};

const DeliveryDocumentUploadModal = ({ 
    isOpen, 
    onClose, 
    categories,
    onDocumentSelect,
    uploading,
    deliveryId,
    partnerName,
}) => {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [namingPayload, setNamingPayload] = useState(null);
    const [dropError, setDropError] = useState('');
    const fileInputRef = useRef(null);

    /* Both doors into this form — the file picker and the drop zone — land
       here, so a file chosen either way is judged by the same rules. */
    const acceptFile = (file) => {
        const reason = rejectionReasonFor(file);
        if (reason) {
            setDropError(reason);
            return;
        }
        setDropError('');
        setSelectedFile(file);
        setNamingPayload(null);
    };

    const handleFileSelect = (event) => {
        acceptFile(event.target.files[0]);
    };

    const resetAll = () => {
        setSelectedCategory(null);
        setSelectedDocumentType('');
        setSelectedFile(null);
        setNamingPayload(null);
        setDropError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    
    const handleCancel = () => {
        onClose();
        resetAll();
    };
    
    const handleConfirm = () => {
        if (!selectedFile || !selectedDocumentType) return;
        onDocumentSelect(selectedDocumentType, selectedFile, namingPayload);
        onClose();
        resetAll();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (filename) => {
        if (!filename) return faFile;
        const extension = filename.split('.').pop().toLowerCase();
        if (extension === 'pdf') return faFilePdf;
        if (['doc', 'docx'].includes(extension)) return faFileWord;
        if (['xls', 'xlsx'].includes(extension)) return faFileExcel;
        if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'].includes(extension)) return faFileImage;
        if (['html', 'css', 'js', 'jsx', 'ts', 'tsx', 'json', 'xml', 'md', 'adoc'].includes(extension)) return faFileCode;
        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return faFileArchive;
        return faFile;
    };
    
    if (!isOpen) return null;

    const showNamingForm = selectedFile && selectedDocumentType;

    // Rendered at the top of the page, not where the host puts it. A host
    // column that stays put while the page scrolls (the delivery's paperwork
    // column) keeps everything inside it on its own layer, so a pop-up left
    // there sits UNDER the delivery's date boxes however high it is raised.
    return createPortal(
        <div className="delivery-doc-upload-modal-overlay" onClick={handleCancel}>
            <div className="delivery-doc-upload-modal" onClick={(e) => e.stopPropagation()}>
                <div className="delivery-doc-upload-modal-header">
                    <h3 className="delivery-doc-upload-modal-title">Upload Delivery Document</h3>
                    <button 
                        className="delivery-doc-upload-close-button"
                        onClick={handleCancel}
                        aria-label="Close modal"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="delivery-doc-upload-modal-body">
                    {/* Step 1: Category Selection */}
                    {!selectedCategory && (
                        <div className="delivery-doc-upload-form-group">
                            <label className="delivery-doc-upload-label">
                                Select Category: <span className="delivery-doc-upload-label-required">*</span>
                            </label>
                            <div className="delivery-doc-category-buttons">
                                {categories.map(cat => (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        className="delivery-doc-category-btn"
                                        style={{ 
                                            '--cat-color': cat.color,
                                            '--cat-color-hover': cat.color + 'dd'
                                        }}
                                        onClick={() => setSelectedCategory(cat)}
                                        disabled={uploading}
                                    >
                                        <span className="delivery-doc-category-label">{smartCaseNode(cat.label)}</span>
                                        <span className="delivery-doc-category-count">{cat.types.length} types</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 2: Sub-type + File (after category selected) */}
                    {selectedCategory && (
                        <>
                            <div className="delivery-doc-upload-form-group">
                                <button
                                    type="button"
                                    className="delivery-doc-back-btn"
                                    onClick={() => {
                                        setSelectedCategory(null);
                                        setSelectedDocumentType('');
                                    }}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} style={{ marginRight: '0.5rem' }} />
                                    Back to categories
                                </button>
                                <div 
                                    className="delivery-doc-selected-category"
                                    style={{ borderLeftColor: selectedCategory.color, backgroundColor: selectedCategory.color + '10' }}
                                >
                                    <span 
                                        className="delivery-doc-selected-category-badge"
                                        style={{ backgroundColor: selectedCategory.color }}
                                    >
                                        {selectedCategory.label}
                                    </span>
                                </div>
                            </div>

                            <div className="delivery-doc-upload-form-group">
                                <label className="delivery-doc-upload-label">
                                    Select Document Type: <span className="delivery-doc-upload-label-required">*</span>
                                </label>
                                <select 
                                    value={selectedDocumentType}
                                    onChange={(e) => setSelectedDocumentType(e.target.value)}
                                    className="delivery-doc-upload-select"
                                    disabled={uploading}
                                >
                                    <option value="">-- Select Document Type --</option>
                                    {selectedCategory.types.map(docType => (
                                        <option key={docType} value={docType}>
                                            {docType}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="delivery-doc-upload-form-group">
                                <label className="delivery-doc-upload-label">
                                    Select File: <span className="delivery-doc-upload-label-required">*</span>
                                </label>
                                <FileDropZone
                                    className={`delivery-doc-upload-drop-zone ${uploading ? 'uploading' : ''}`}
                                    disabled={uploading}
                                    onFiles={(files) => acceptFile(files[0])}
                                    onError={setDropError}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        onChange={handleFileSelect}
                                        accept={ACCEPT_ATTRIBUTE}
                                        style={{ display: 'none' }}
                                        disabled={uploading}
                                    />
                                    
                                    {selectedFile ? (
                                        <div className="delivery-doc-upload-file-preview">
                                            <FontAwesomeIcon 
                                                icon={getFileIcon(selectedFile.name)} 
                                                className="delivery-doc-upload-file-icon"
                                            />
                                            <div className="delivery-doc-upload-file-info">
                                                <div className="delivery-doc-upload-file-name">
                                                    {selectedFile.name}
                                                </div>
                                                <div className="delivery-doc-upload-file-size">
                                                    {formatFileSize(selectedFile.size)}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="delivery-doc-upload-placeholder">
                                            <div className="delivery-doc-upload-placeholder-title">
                                                Drop files here or click to browse
                                            </div>
                                            <div className="delivery-doc-upload-placeholder-text">
                                                Supports: PDF, DOC, DOCX, XLS, XLSX, PPTX, Markdown, HTML, CSV, Images, TXT, EML, MSG, AsciiDoc
                                            </div>
                                        </div>
                                    )}
                                </FileDropZone>
                                {dropError && (
                                    <div className="delivery-doc-upload-drop-error" role="alert">
                                        {dropError}
                                    </div>
                                )}
                                <div className="delivery-doc-upload-file-hint">
                                    Maximum file size: 30MB
                                </div>
                            </div>

                            {showNamingForm && (
                                <DocumentNameSuggestionForm
                                    file={selectedFile}
                                    module="delivery"
                                    relatedEntityId={deliveryId}
                                    typeDropdownValue={selectedDocumentType}
                                    partnerName={partnerName || ''}
                                    onChange={setNamingPayload}
                                    disabled={uploading}
                                />
                            )}
                        </>
                    )}
                </div>

                <div className="delivery-doc-upload-modal-footer">
                    <button 
                        className="delivery-doc-upload-btn delivery-doc-upload-btn-secondary"
                        onClick={handleCancel}
                        disabled={uploading}
                    >
                        Cancel
                    </button>
                    <button 
                        className="delivery-doc-upload-btn delivery-doc-upload-btn-primary"
                        onClick={handleConfirm}
                        /* Nothing is read before the upload any more (ClickUp
                           1248h53bmhy): the document is saved under its own
                           name and the suggestion comes afterwards, so the
                           upload is never held up for a name. ``isLoading``
                           stays as a guard in case that ever changes. */
                        disabled={
                            uploading
                            || !selectedFile
                            || !selectedDocumentType
                            || !!namingPayload?.isLoading
                        }
                    >
                        {uploading ? 'Uploading...' : 'Upload Document'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DeliveryDocumentUploadModal;
